<script setup>
    import { ArrowDownTrayIcon } from '@heroicons/vue/24/outline';
    import { ref, computed, onMounted } from 'vue';
    import DropZone from '../components/DropZone.vue';
    import FilesListPreview from '../components/FilesListPreview.vue';
    import useFileList from '../compositions/file-list'
    import { useCloudStore } from '../stores/cloud';
    const { files, addFiles, removeFile } = useFileList()

    const cloudStore = useCloudStore();

    const adding_document = ref(false);
    const categories = ref([]);
    const activeCategory = ref(null); // null means "all"
    const loadingCategories = ref(false);
    const selectedCategory = ref('');
    const creatingCategory = ref(false);
    const newCategoryName = ref('');

    const loadingDocument = ref(false);

    const documents = ref([]);

    const categoryOptions = computed(() => {
        return [
            ...categories.value,
            { id: '__new__', label: 'Créer une nouvelle catégorie' }
        ]
    })

    const loadCategories = async () => {
        const categoryLabels = [];
        documents.value.forEach(doc => {    
            categoryLabels.push(doc.category);
        });

        const uniq_categories = [...new Set(categoryLabels)];
        const categoriesData = uniq_categories.map((el, index) => {
            return {id: index, label: el}
        })

        categories.value = categoriesData;
        selectedCategory.value = categoriesData[0]?.label ?? ''
    }

    const handleDocument = async (e) => {
        const formData = new FormData(e.target);
        formData.set('category', selectedCategory.value);

        if (files.value && files.value.length) {
            formData.set('file', files.value[0].file, files.value[0].file.name);
        }

        for (const pair of formData.entries()) console.log(pair[0], pair[1]);

        try {
            const result = await cloudStore.addDocuments(formData);
            console.log(result);
        } catch (error) {
            console.error(error);
        } finally {
            adding_document.value = false;
            await getDocuments();
        }
    };

    const onInputChange = (e) => {
        addFiles(e.target.files)
        e.target.value = null
    }

    const handleCategoryChange = (value) => {
        if (value === '__new__') {
            creatingCategory.value = true
            newCategoryName.value = ''
        } else {
            creatingCategory.value = false
        }
    }

    const getDocuments = async () => {
        loadingDocument.value = true;
        try {
            const result = await cloudStore.getDocuments()
            console.log(result)
            if(result.result.length > 0){
                documents.value = result.result;
                await loadCategories();
            }
        } catch(error){
            console.error(error)
        } finally {
            loadingDocument.value = false;
        }
    }
    
    const filteredDocuments = computed(() => {
        if (!activeCategory.value) {
            return documents.value;
        }
        return documents.value.filter(doc => doc.category === activeCategory.value);
    });

    const addNewCategory = () => {
        const label = newCategoryName.value.trim()
        if (!label) return
        const newId = label
        categories.value = [...categories.value, { id: newId, label }]
        selectedCategory.value = label
        creatingCategory.value = false
        newCategoryName.value = ''
    }

    onMounted(async () => {
        await getDocuments();
    })
</script>

<template>
    <div class="px-4 sm:px-6 lg:px-8 overflow-auto h-screen pb-24 max-w-7xl mx-auto">
        <div class="border-b border-gray-200 pb-5 mt-12 flex flex-wrap justify-between items-end">
            <div>
                <h3 class="text-3xl font-semibold leading-6 text-gray-900 mb-4">Espace document</h3>
                <p class="text-pretty text-base font-normal text-gray-500">Accédez d’un seul coup d’œil aux nouveaux documents chartés.</p>
            </div>

            <div class="sm:mt-0 mt-5">
                <button @click="() => adding_document = true" v-if="!adding_document" class="px-3 py-2 bg-rose-500 rounded-lg text-sm text-white flex items-center gap-x-3">
                    Ajouter un document
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 11H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M11 15V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 21H14C19 21 21 19 21 14V8C21 3 19 1 14 1H8C3 1 1 3 1 8V14C1 19 3 21 8 21Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
        
        <Transition
			mode="out-in"
			appear
			enter-from-class="opacity-0 translate-x-4"
			enter-active-class="transition duration-300 ease-out"
			enter-to-class="opacity-100 translate-x-0"
			leave-from-class="opacity-100 translate-x-0"
			leave-active-class="transition duration-200 ease-in"
			leave-to-class="opacity-0 translate-x-4"
		>
            <section v-if="!adding_document">
                <template v-if="documents.length > 0">
                    <div v-if="categories.length > 0" class="flex items-center mt-8">
                        <button
                            @click="activeCategory = null"
                            :class="['rounded-tl-lg rounded-bl-lg px-3 py-1.5 border border-text-gray-400 mx-[-1px]', activeCategory === null ? 'bg-rose-500 text-white' : 'bg-white']">
                            Tout
                        </button>
                        <template v-for="(category, index) in categories" :key="category.id">
                            <button
                                @click="activeCategory = category.label"
                                :class="[
                                    'px-3 py-1.5 border border-text-gray-400 mx-[-1px]',
                                    index === categories.length - 1 ? 'rounded-tr-lg rounded-br-lg' : '',
                                    activeCategory === category.label ? 'bg-rose-500 text-white' : 'bg-white'
                                ]">
                                {{ category.label }}
                            </button>
                        </template>
                    </div>
                    
                    <div class="grid grid-cols-1 sm:grid-cols-4 gap-x-2 gap-y-3 mt-5">
                        <div v-for="document in filteredDocuments" :key="document.id" class="border border-text-gray rounded-lg px-5 py-4">
                            <div class="aspect-square">
                                <img :src="document.thumbnail" alt="">
                            </div>
                            <span class="border-b border-b-gray-400/30 block pb-1 text-sm text-gray-400">
                                {{ document.category }}
                            </span>
                            <h3 class="font-semibold text-rose-500 mt-5">{{ document.label }}</h3>
                            <a :href="document.url" target="_blank" class="inline-flex items-center gap-x-1 rounded-lg border border-text-400 px-2 py-1 mt-3">
                                Télécharger
                                <ArrowDownTrayIcon class="size-5" />
                            </a>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <h2 class="text-center">Aucun document disponible pour le moment...</h2>
                </template>
            </section>
            <form v-else method="POST" @submit.prevent="handleDocument" class="mt-5">
                <div class="space-y-12">
                    <TransitionGroup class="overflow-hidden flex flex-col items-center" tag="div" name="deploy">
                        <div class="grid w-full max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <DropZone class="drop-area mt-4 col-span-full" @files-dropped="addFiles" #default="{ dropZoneActive }">
                                <div v-show="!files.length" :class="[dropZoneActive ? 'border-solid border-rose' : 'border-dashed','mt-2 flex justify-center rounded-lg border border-gray-900/25 px-6 py-10']">
                                    <div class="text-center">
                                        <PhotoIcon class="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                                        <div class="mt-4 flex text-sm leading-6 text-gray-600">
                                            <label for="file-upload" class="relative cursor-pointer rounded-md bg-white font-semibold text-rose-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-rose-600 focus-within:ring-offset-2 hover:text-rose-500">
                                                <span>Téléverser un document</span>
                                                <input id="file-upload" @change="onInputChange" name="file-upload" type="file" class="sr-only" />
                                            </label>
                                            <p class="pl-1">ou glisser, déposer</p>
                                        </div>
                                        <p class="text-xs leading-5 text-gray-600">PDF, PNG, JPG, DOCX, jusqu'à 10 Mo...</p>
                                    </div>
                                </div>

                                <ul v-show="files.length">
                                    <FilesListPreview v-for="file of files" :key="file.id" :file="file" tag="li" @remove="removeFile" />
                                </ul>
                            </DropZone>

                            <div class="sm:col-span-full">
                                <label for="label"
                                    class="block text-sm font-medium leading-6 text-gray-900">Nom du document</label>
                                <div class="mt-2">
                                    <input type="text" name="label"
                                        id="label" required
                                        class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6" />
                                </div>
                            </div>
                            
                            <div class="sm:col-span-full">
                                <label for="category"
                                    class="block text-sm font-medium leading-6 text-gray-900">Catégorie</label>
                                <div class="mt-2 space-y-3">
                                    <select id="category" name="category" v-model="selectedCategory" @change="handleCategoryChange($event.target.value)"
                                        class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6">
                                        <option disabled value="">Chargement...</option>
                                        <option v-for="cat in categoryOptions" :key="cat.label" :value="cat.label">
                                            {{ cat.label }}
                                        </option>
                                    </select>
                                    <div v-if="loadingCategories" class="text-sm text-gray-500">Chargement des catégories...</div>
                                    <div v-if="creatingCategory" class="space-y-2">
                                        <input type="text" v-model="newCategoryName" placeholder="Nouvelle catégorie"
                                            class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6" />
                                        <div class="flex items-center gap-3">
                                            <button type="button" @click="addNewCategory"
                                                class="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600 duration-300 ease-out">
                                                Ajouter la catégorie
                                            </button>
                                            <button type="button" @click="creatingCategory = false"
                                                class="text-sm text-gray-500 hover:text-gray-700">
                                                Annuler
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="flex flex-wrap justify-end items-center mt-1 gap-x-5 col-span-full">
                                <span class="text-red-400 text-sm font-semibold" v-if="error">{{ error }}</span>
                                <div class="flex items-center justify-end gap-x-2">
                                    <button type="button" @click="adding_document = false"
                                        class="rounded-md px-3 py-2 text-sm font-semibold text-gray-black shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 duration-300 ease-out border border-gray-400 ">
                                        Annuler
                                    </button>
                                    <button type="submit" :class="[loadingDocument ? 'animate-pulse pointer-events-none' : 'pointer-events-auto' , 'rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600 duration-300 ease-out']">Enregister le document</button>
                                </div>
                            </div>
                        </div>
                    </TransitionGroup>
                </div>
            </form>
        </Transition>
    </div>
</template>
